import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Ruler, Truck, ShieldCheck } from "lucide-react";
import { useCart, useLang } from "../store";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SneakerSection() {
  const { lang } = useLang();
  const isEs = lang === "es";
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => {
      const all = r.data.products || [];
      setProducts(all.filter((p) => p.category === "sneaker"));
    });
  }, []);

  if (!products.length) return null;
  const p = products[0];

  return (
    <section
      id="sneakers"
      className="bg-[#EFECE6] border-b-[3px] border-[#0C0A09]"
      data-testid="sneaker-section"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <span className="nb-badge mb-4">
              {isEs ? "👟 Edición limitada" : "👟 Limited edition"}
            </span>
            <h2 className="font-display text-5xl md:text-7xl leading-none tracking-tight mt-3">
              {isEs ? "Chunky Sneaker" : "Chunky Sneaker"}
            </h2>
          </div>
          <p className="max-w-md text-lg font-medium">
            {isEs
              ? "Una única pieza disponible. Diseño chunky, colorway burdeos y verde salvia, suela dentada."
              : "One-of-one available. Chunky build, burgundy + sage colorway, aggressive tread sole."}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          {/* Image */}
          <div className="lg:col-span-7">
            <div className="relative bg-white border-[3px] border-[#0C0A09] shadow-[12px_12px_0_0_#0C0A09] overflow-hidden">
              <img
                src="/sneakers/sneaker-angle.avif"
                alt={isEs ? p.name_es : p.name_en}
                className="w-full h-[420px] md:h-[560px] object-contain p-8"
                data-testid="sneaker-hero-img"
              />
              <div className="absolute top-4 left-4 bg-[#7A2E36] text-white border-[3px] border-[#0C0A09] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em]">
                -24%
              </div>
              <div className="absolute bottom-4 right-4 bg-[#9BE800] border-[3px] border-[#0C0A09] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em]">
                {isEs ? "⚡ Pocas unidades" : "⚡ Few units left"}
              </div>
            </div>

            {/* Extra images row */}
            <div className="mt-4 grid grid-cols-3 gap-4">
              {["/sneakers/sneaker-side.avif", "/sneakers/sneaker-main.webp", "/sneakers/sneaker-angle.avif"].map((src, i) => (
                <div key={i} className="aspect-square bg-white border-[3px] border-[#0C0A09] shadow-[4px_4px_0_0_#0C0A09] overflow-hidden">
                  <img src={src} alt={`Sneaker ${i + 1}`} className="w-full h-full object-contain p-3" />
                </div>
              ))}
            </div>
          </div>

          {/* Buy box */}
          <div className="lg:col-span-5">
            <div className="bg-white border-[3px] border-[#0C0A09] shadow-[8px_8px_0_0_#0C0A09] p-6 md:p-8">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-70">
                {isEs ? "Zapatilla premium" : "Premium sneaker"}
              </div>
              <div className="font-display text-3xl md:text-4xl leading-none mt-2">
                {isEs ? p.name_es : p.name_en}
              </div>

              <div className="mt-5 flex items-baseline gap-3">
                <span className="font-display text-5xl">€{p.price.toFixed(0)}</span>
                {p.original_price && p.original_price > p.price && (
                  <>
                    <span className="font-display text-2xl line-through opacity-50">
                      €{p.original_price.toFixed(0)}
                    </span>
                    <span className="px-2 py-1 bg-[#7A2E36] text-white border-[2px] border-[#0C0A09] rounded-full text-[10px] font-bold uppercase tracking-widest">
                      -24%
                    </span>
                  </>
                )}
              </div>

              {/* Size box */}
              <div className="mt-6 bg-[#FAF9F6] border-[3px] border-[#0C0A09] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Ruler size={16} strokeWidth={2.75} />
                  <div className="text-xs font-bold uppercase tracking-[0.2em]">
                    {isEs ? "Talla disponible" : "Available size"}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {["38", "39", "40", "41", "42", "43"].map((size) => {
                    const available = size === "40";
                    return (
                      <div
                        key={size}
                        className={`w-14 h-14 border-[3px] border-[#0C0A09] flex items-center justify-center font-display text-lg ${
                          available
                            ? "bg-[#9BE800] shadow-[3px_3px_0_0_#0C0A09]"
                            : "bg-white opacity-40 line-through"
                        }`}
                      >
                        {size}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 text-[11px] font-bold uppercase tracking-widest text-[#7A2E36]">
                  {isEs ? "⚡ ¡Quedan pocas unidades!" : "⚡ Few units left!"}
                </div>
              </div>

              <button
                data-testid="add-to-cart-sneaker"
                onClick={() => addToCart(p)}
                className="nb-btn mt-6 w-full justify-center"
              >
                <Plus size={18} strokeWidth={3} /> {isEs ? `Agregar — €${p.price.toFixed(0)}` : `Add to cart — €${p.price.toFixed(0)}`}
              </button>

              {/* Product details */}
              <div className="mt-5 bg-[#FAF9F6] border-[3px] border-[#0C0A09] p-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3">
                  {isEs ? "Detalles del producto" : "Product details"}
                </div>
                <ul className="text-sm font-medium leading-relaxed space-y-1.5">
                  <li>• {isEs ? "Estampado Multicolor" : "Multicolor print"}</li>
                  <li>• {isEs ? "Tela técnica, interior de tejido" : "Technical fabric, textile lining"}</li>
                  <li>• {isEs ? "Cierre con cordones" : "Lace closure"}</li>
                  <li>• {isEs ? "Puntera redonda" : "Round toe"}</li>
                  <li>• {isEs ? "Logotipo de la marca" : "Brand logo"}</li>
                  <li>• {isEs ? "Composición: 100% Poliéster, Poliuretano" : "Composition: 100% Polyester, Polyurethane"}</li>
                  <li>• {isEs ? "Altura de la suela: 4 cm" : "Sole height: 4 cm"}</li>
                </ul>
                <div className="mt-3 pt-3 border-t-[2px] border-[#0C0A09]/20 text-[10px] font-bold uppercase tracking-widest opacity-70">
                  {isEs ? "Ref:" : "Ref:"} 32023657OF
                </div>
                <div className="mt-2 text-[10px] font-medium opacity-60 leading-relaxed">
                  {isEs
                    ? "* Medidas basadas en talla 10.5 UK (44.5 EU). Consulta la guía de tallas antes de comprar."
                    : "* Measurements based on size 10.5 UK (44.5 EU). Check size guide before buying."}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 bg-[#FAF9F6] border-[2px] border-[#0C0A09] px-3 py-2">
                  <Truck size={14} strokeWidth={2.75} />
                  <span className="text-[10px] font-bold uppercase tracking-wider">
                    {isEs ? "Envío 24h" : "24h ship"}
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-[#FAF9F6] border-[2px] border-[#0C0A09] px-3 py-2">
                  <ShieldCheck size={14} strokeWidth={2.75} />
                  <span className="text-[10px] font-bold uppercase tracking-wider">
                    {isEs ? "Autenticidad" : "Authentic"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
